import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Group as GroupIcon,
  Policy as PolicyIcon,
  AccountCircle as AccountCircleIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useAuth, useDrawer } from '../../contexts/AuthContext';

const drawerWidth = 280;

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  description: string;
}

const menuItems: MenuItem[] = [
  {
    id: 'viewer',
    label: 'Document Viewer',
    icon: <DashboardIcon />,
    path: '/viewer',
    description: 'View documents and files'
  },
  {
    id: 'workflow',
    label: 'Workflow',
    icon: <GroupIcon />,
    path: '/workflow',
    description: 'Manage workflows'
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: <SecurityIcon />,
    path: '/notifications',
    description: 'View notifications'
  },
  {
    id: 'go-metadata',
    label: 'Metadata',
    icon: <AccountCircleIcon />,
    path: '/go-metadata',
    description: 'Document metadata'
  },
  {
    id: 'sap-analytics',
    label: 'SAP Analytics',
    icon: <DashboardIcon />,
    path: '/sap-analytics',
    description: 'SAP analytics dashboard'
  },
  {
    id: 'sap-integration',
    label: 'SAP Integration',
    icon: <GroupIcon />,
    path: '/sap-integration',
    description: 'SAP integration tools'
  },
  {
    id: 'dashboard',
    label: 'Admin Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    description: 'Admin overview and validation'
  },
  {
    id: 'groups',
    label: 'Groups',
    icon: <GroupIcon />,
    path: '/groups',
    description: 'Manage user groups'
  },
  {
    id: 'permissions',
    label: 'Redaction Restrictions',
    icon: <SecurityIcon />,
    path: '/permissions',
    description: 'Manage redaction restrictions'
  },
  {
    id: 'users',
    label: 'Users',
    icon: <AccountCircleIcon />,
    path: '/users',
    description: 'User management'
  },
  {
    id: 'logout',
    label: 'Logout',
    icon: <LogoutIcon />,
    path: '#logout',
    description: 'Sign out'
  },
];

const Layout: React.FC = () => {
  // const [drawerOpen, setDrawerOpen] = useState(false);
  const { drawerOpen, openDrawer, closeDrawer } = useDrawer();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const isAdmin = user?.username === 'admin';
  
  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  } | null>(null);

  const currentMenuItem = menuItems.find(item => item.path === location.pathname) || menuItems[0];
  const isDocumentViewer = location.pathname === '/document-viewer';

  // Check for login message on component mount
  useEffect(() => {
    const loginMessage = sessionStorage.getItem('loginMessage');
    if (loginMessage) {
      setNotification({
        message: loginMessage,
        severity: 'success'
      });
      sessionStorage.removeItem('loginMessage');
    }
  }, []);

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleMenuItemClick = (path: string) => {
    if (path === '#logout') {
      logout();
      navigate('/login');
      closeDrawer();
      return;
    }
    navigate(path);
    closeDrawer();
  };

  const drawer = (
    <Box sx={{ width: drawerWidth }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
          DataUnify Intelligent Viewer
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome to DU-IV
        </Typography>
      </Box>
      
      <List sx={{ pt: 1 }}>
        {menuItems
          .filter(item => {
            // Mostrar opciones generales para todos
            if (['viewer', 'workflow', 'notifications', 'go-metadata', 'sap-analytics', 'sap-integration', 'logout'].includes(item.id)) {
              return true;
            }
            // Solo mostrar opciones de admin si el usuario es admin
            if (['dashboard', 'groups', 'permissions', 'users'].includes(item.id)) {
              return isAdmin;
            }
            return true;
          })
          .map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleMenuItemClick(item.path)}
              sx={{
                mx: 1,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                }}
                secondaryTypographyProps={{
                  fontSize: '0.75rem',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={openDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Chip
              label={currentMenuItem.label}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          
          {/* Título central */}
          <Box sx={{ 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
              DataUnify Intelligent Viewer
            </Typography>
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ position: 'absolute', right: 16, top: 8, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {user?.username}
            </Typography>
            <IconButton color="inherit" onClick={() => { logout(); navigate('/login'); }}>
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: 'block',
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isDocumentViewer ? 0 : 3,
          width: isDocumentViewer ? '100%' : { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
        }}
      >
        <Outlet />
      </Box>

      {/* Global Notification Snackbar */}
      {notification && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Layout; 